import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal, Loader2 } from "lucide-react";

const SORTS = [
  ["popular", "Popular"], ["newest", "Newest"], ["price_low", "Price: Low to High"],
  ["price_high", "Price: High to Low"], ["discount", "Highest Discount"], ["rating", "Best Rated"],
];
const DIET = ["Vegan", "Vegetarian", "Gluten-Free", "Organic", "Sugar-Free", "Protein-Rich"];

function Filters({ cats, brands, state, setState, apply }) {
  const toggleTag = (t) => {
    const tags = state.tags.includes(t) ? state.tags.filter((x) => x !== t) : [...state.tags, t];
    setState({ ...state, tags });
  };
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-heading text-lg text-forest mb-3">Category</h4>
        <div className="space-y-1.5 max-h-52 overflow-auto hide-scrollbar pr-1">
          <button onClick={() => setState({ ...state, category: "" })} className={`block text-sm text-left w-full py-1 ${!state.category ? "text-terracotta font-600" : "text-brown"}`}>All Categories</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setState({ ...state, category: c.id })} data-testid={`filter-cat-${c.id}`}
              className={`block text-sm text-left w-full py-1 ${state.category === c.id ? "text-terracotta font-600" : "text-brown hover:text-forest"}`}>{c.name} <span className="text-muted-foreground">({c.product_count})</span></button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-heading text-lg text-forest mb-3">Price: up to ₹{state.maxPrice}</h4>
        <Slider min={0} max={1500} step={50} value={[state.maxPrice]} onValueChange={([v]) => setState({ ...state, maxPrice: v })} data-testid="price-slider" />
      </div>
      <div>
        <h4 className="font-heading text-lg text-forest mb-3">Dietary</h4>
        <div className="space-y-2">
          {DIET.map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm text-brown cursor-pointer">
              <Checkbox checked={state.tags.includes(t)} onCheckedChange={() => toggleTag(t)} data-testid={`filter-tag-${t}`} /> {t}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-heading text-lg text-forest mb-3">Brand</h4>
        <Select value={state.brand || "all"} onValueChange={(v) => setState({ ...state, brand: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-xl bg-white" data-testid="filter-brand"><SelectValue placeholder="All Brands" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Brands</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm text-brown cursor-pointer">
        <Checkbox checked={state.inStock} onCheckedChange={(v) => setState({ ...state, inStock: !!v })} data-testid="filter-instock" /> In stock only
      </label>
      <Button onClick={apply} className="w-full rounded-full bg-forest hover:bg-earth text-cream" data-testid="apply-filters-btn">Apply Filters</Button>
    </div>
  );
}

export default function Shop() {
  const [sp, setSp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(sp.get("sort") || (sp.get("category") ? "newest" : "popular"));
  const [page, setPage] = useState(1);
  const [state, setState] = useState({
    category: sp.get("category") || "", brand: "", tags: [], maxPrice: 1500, inStock: false,
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  const fetchProducts = useCallback((pageArg = 1) => {
    setLoading(true);
    const params = new URLSearchParams();
    const search = sp.get("search"); if (search) params.set("search", search);
    const wg = sp.get("wellness_goal"); if (wg) params.set("wellness_goal", wg);
    const section = sp.get("section"); if (section) params.set("section", section);
    if (state.category) params.set("category", state.category);
    if (state.brand) params.set("brand", state.brand);
    if (state.tags.length) params.set("tags", state.tags.join(","));
    if (state.maxPrice < 1500) params.set("max_price", state.maxPrice);
    if (state.inStock) params.set("in_stock", "true");
    params.set("sort", sort);
    params.set("page", pageArg);
    params.set("limit", "12");
    api.get(`/products?${params.toString()}`).then((r) => { setData(r.data); setPage(pageArg); }).finally(() => setLoading(false));
  }, [sp, state, sort]);

  useEffect(() => { fetchProducts(1); /* eslint-disable-next-line */ }, [sp, sort]);
  useEffect(() => { setState((s) => ({ ...s, category: sp.get("category") || "" })); }, [sp]);
  useEffect(() => {
    if (sp.get("sort")) return;
    const catId = sp.get("category");
    if (!catId || !cats.length) return;
    const c = cats.find((x) => x.id === catId);
    if (c?.default_sort) setSort(c.default_sort);
  }, [cats, sp]);

  const heading = sp.get("search") ? `Results for "${sp.get("search")}"` : sp.get("section") ? "Featured Products" : "Shop All Products";

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">{heading}</h1>
          <p className="text-sm text-muted-foreground mt-1">{data.total} products found</p>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden rounded-full border-sage" data-testid="mobile-filters-btn"><SlidersHorizontal className="w-4 h-4 mr-1" /> Filters</Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-cream overflow-auto"><div className="pt-6"><Filters cats={cats} brands={brands} state={state} setState={setState} apply={() => fetchProducts(1)} /></div></SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] rounded-full bg-white border-sage" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="bg-white rounded-2xl soft-shadow p-5 sticky top-28"><Filters cats={cats} brands={brands} state={state} setState={setState} apply={() => fetchProducts(1)} /></div>
        </aside>
        <div className="flex-1">
          {loading ? (
            <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-32 text-muted-foreground">No products match your filters.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {data.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <Button variant="outline" disabled={page <= 1} onClick={() => fetchProducts(page - 1)} className="rounded-full" data-testid="prev-page-btn">Prev</Button>
                  <span className="text-sm text-brown px-3">Page {page} of {data.pages}</span>
                  <Button variant="outline" disabled={page >= data.pages} onClick={() => fetchProducts(page + 1)} className="rounded-full" data-testid="next-page-btn">Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
